import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Image, Info, Map, MapPin, Calendar, Camera, Settings, Eye, Download, Share2, AlertTriangle, FileQuestion, Filter, Layers, Tag, X, UserX, Upload, RefreshCw } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов страницы EXIF-анализатора
import ImageUploader from './ExifDataReaderPage/components/ImageUploader';
import ExifOverview from './ExifDataReaderPage/components/ExifOverview';
import ExifDetailsPanel from './ExifDataReaderPage/components/ExifDetailsPanel';
import LocationViewer from './ExifDataReaderPage/components/LocationViewer';
import ExifPrivacyChecker from './ExifDataReaderPage/components/ExifPrivacyChecker';
import ExifEditingTools from './ExifDataReaderPage/components/ExifEditingTools';
import InfoContent from './ExifDataReaderPage/components/InfoContent';
import PopularCamerasSidebar from './ExifDataReaderPage/components/PopularCamerasSidebar';
import PrivacyTipsSidebar from './ExifDataReaderPage/components/PrivacyTipsSidebar';
import RecentFilesSidebar from './ExifDataReaderPage/components/RecentFilesSidebar';

/**
 * ExifDataReaderPage - Страница для чтения, анализа и редактирования EXIF-данных изображений
 */
const ExifDataReaderPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('exif-data-reader'), []);
  const pageTitle = tool?.metaTitle || 'Считыватель EXIF-данных | Gensio';
  const metaDescription = tool?.metaDescription || 'Бесплатный онлайн считыватель EXIF-данных для фотографий. Узнайте все о технических параметрах своих снимков: камера, объектив, настройки съемки, GPS-координаты, дата и время съемки.';

  // --- Управление состоянием ---
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [exifData, setExifData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'camera', 'location', 'privacy', 'edit'
  const [recentFiles, setRecentFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedExifData, setEditedExifData] = useState(null);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка недавно просмотренных файлов из localStorage ---
  useEffect(() => {
    const storedRecentFiles = localStorage.getItem('exifRecentFiles');
    if (storedRecentFiles) {
      try {
        setRecentFiles(JSON.parse(storedRecentFiles));
      } catch (e) {
        console.error("Ошибка при загрузке недавних файлов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки ---
  useEffect(() => {
    if (!subToolId) return;

    switch (subToolId) {
      case 'privacy':
        setActiveTab('privacy');
        break;
      case 'location':
        setActiveTab('location');
        break;
      case 'camera':
        setActiveTab('camera');
        break;
      case 'editor':
        setActiveTab('edit');
        break;
      default:
        setActiveTab('general');
    }
  }, [subToolId]);

  // --- Функция анализа EXIF-данных ---
  const analyzeExifData = async (file) => {
    if (!file) return;

    setIsLoading(true);
    setError(null);
    
    try {
      // Создаем URL для отображения изображения
      const objectUrl = URL.createObjectURL(file);
      setImageUrl(objectUrl);
      setImageFile(file);

      // Здесь должен быть код для извлечения EXIF-данных
      // На реальном проекте мы бы использовали библиотеку, например, exif-js
      // Для этого примера мы симулируем задержку и создаем тестовые данные
      
      await new Promise(resolve => setTimeout(resolve, 500)); // Имитация обработки

      // Создаем примерные данные (в реальном приложении здесь будут данные из библиотеки)
      const mockExifData = generateMockExifData(file.name);
      setExifData(mockExifData);
      setEditedExifData(mockExifData); // Копия для редактирования

      // Добавляем файл в список недавних
      addToRecentFiles({
        name: file.name,
        size: file.size,
        type: file.type,
        url: objectUrl,
        thumbnail: objectUrl,
        date: new Date().toISOString(),
        exifPreview: {
          camera: mockExifData.camera.model,
          date: mockExifData.date.original,
          hasLocation: mockExifData.location.latitude !== null
        }
      });
    
    } catch (err) {
      console.error("Ошибка при анализе EXIF:", err);
      setError("Не удалось извлечь EXIF-данные. Файл может быть поврежден или не содержать метаданных.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Функция для генерации тестовых EXIF-данных --- 
  // В реальном приложении это будет заменено на данные из библиотеки EXIF
  const generateMockExifData = (fileName) => {
    const randomCamera = getRandomCamera();
    const dateCreated = new Date();
    dateCreated.setMonth(dateCreated.getMonth() - Math.floor(Math.random() * 6));
    
    // Создаем координаты где-то в России
    const hasLocation = Math.random() > 0.3; // 70% шанс, что будут координаты
    const randomLat = hasLocation ? 55.75 + (Math.random() * 2 - 1) : null;
    const randomLng = hasLocation ? 37.62 + (Math.random() * 2 - 1) : null;
    
    const locationName = hasLocation ? "Москва, Россия" : null;
    
    return {
      file: {
        name: fileName,
        size: Math.floor(Math.random() * 10000000),
        type: "image/jpeg",
        lastModified: dateCreated.getTime()
      },
      image: {
        width: 3000 + Math.floor(Math.random() * 3000),
        height: 2000 + Math.floor(Math.random() * 2000),
        orientation: "horizontal",
        colorSpace: "sRGB",
        format: "JPEG",
        compression: "JPEG (old-style)",
        bitDepth: 24
      },
      camera: {
        make: randomCamera.make,
        model: randomCamera.model,
        software: randomCamera.software || "Adobe Photoshop",
        lensModel: randomCamera.lens,
        lensSerialNumber: Math.random().toString(36).substring(2, 10).toUpperCase()
      },
      settings: {
        focalLength: Math.floor(Math.random() * 150) + 18,
        focalLength35mm: Math.floor(Math.random() * 150) + 27,
        aperture: (Math.random() * 2 + 1.4).toFixed(1),
        shutterSpeed: "1/" + (Math.pow(2, Math.floor(Math.random() * 10))),
        iso: [100, 200, 400, 800, 1600, 3200][Math.floor(Math.random() * 6)],
        exposureCompensation: (Math.random() * 2 - 1).toFixed(1),
        exposureProgram: ["Программный", "Приоритет диафрагмы", "Приоритет выдержки", "Ручной"][Math.floor(Math.random() * 4)],
        whiteBalance: ["Авто", "Дневной свет", "Облачно", "Вспышка", "Тень"][Math.floor(Math.random() * 5)],
        meteringMode: ["Оценочный", "Центрально-взвешенный", "Точечный"][Math.floor(Math.random() * 3)]
      },
      date: {
        original: dateCreated.toISOString(),
        digitized: dateCreated.toISOString(),
        modified: new Date().toISOString()
      },
      location: {
        latitude: randomLat,
        longitude: randomLng,
        altitude: hasLocation ? Math.floor(Math.random() * 1000) : null,
        name: locationName,
        country: hasLocation ? "Россия" : null,
        city: hasLocation ? "Москва" : null
      },
      copyright: Math.random() > 0.7 ? "© " + new Date().getFullYear() + " Фотограф" : null,
      author: Math.random() > 0.5 ? "Фотограф Иванов" : null,
      description: Math.random() > 0.8 ? "Описание фотографии" : null,
      keywords: Math.random() > 0.6 ? ["природа", "Россия", "фотография"] : [],
      privacy: {
        hasPersonalData: hasLocation || Math.random() > 0.7,
        riskLevel: hasLocation ? "Высокий" : "Низкий",
        recommendations: hasLocation ? [
          "Удалите данные о местоположении перед публикацией",
          "Проверьте наличие персональных данных в метаданных"
        ] : [
          "Рекомендуется регулярно проверять EXIF-данные перед публикацией фотографий"
        ]
      }
    };
  };

  // --- Популярные камеры в России ---
  const popularCameras = [
    { make: "Canon", model: "EOS 6D Mark II", lens: "Canon EF 24-105mm f/4L IS II USM", software: "Canon Digital Photo Professional" },
    { make: "Nikon", model: "D750", lens: "Nikon 24-70mm f/2.8G ED", software: "Nikon ViewNX-i" },
    { make: "Sony", model: "Alpha A7 III", lens: "Sony FE 24-70mm f/2.8 GM", software: "Sony Imaging Edge" },
    { make: "Canon", model: "EOS R6", lens: "Canon RF 24-70mm F2.8 L IS USM", software: "Canon Digital Photo Professional" },
    { make: "Nikon", model: "Z6", lens: "NIKKOR Z 24-70mm f/4 S", software: "Nikon Capture NX-D" },
    { make: "Sony", model: "Alpha A7R IV", lens: "Sony FE 16-35mm f/2.8 GM", software: "Adobe Lightroom Classic" },
    { make: "Fujifilm", model: "X-T4", lens: "Fujinon XF16-55mmF2.8 R LM WR", software: "Fujifilm X RAW STUDIO" },
    { make: "Canon", model: "EOS 90D", lens: "Canon EF-S 18-135mm f/3.5-5.6 IS USM", software: "Canon Digital Photo Professional" },
    { make: "Nikon", model: "D850", lens: "Nikon 70-200mm f/2.8E FL ED VR", software: "Nikon Capture NX-D" },
    { make: "Зенит", model: "M", lens: "Зенитар 35mm f/1.0", software: null }, // Отечественная камера
    { make: "Xiaomi", model: "Mi 11 Ultra", lens: "Встроенный", software: "MIUI Camera" }, // Популярный в России смартфон
    { make: "iPhone", model: "13 Pro Max", lens: "Apple Wide Lens", software: "iOS Camera" }
  ];

  // Случайная камера для тестовых данных
  const getRandomCamera = () => {
    return popularCameras[Math.floor(Math.random() * popularCameras.length)];
  };

  // --- Функция для добавления файла в список недавних ---
  const addToRecentFiles = (fileInfo) => {
    const updatedRecentFiles = [fileInfo, ...recentFiles.filter(f => f.name !== fileInfo.name)].slice(0, 5);
    setRecentFiles(updatedRecentFiles);
    localStorage.setItem('exifRecentFiles', JSON.stringify(updatedRecentFiles));
  };

  // --- Функция для очистки данных и загрузки нового изображения ---
  const resetAndLoadNew = () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl); // Освобождаем ресурсы
    }
    setImageFile(null);
    setImageUrl(null);
    setExifData(null);
    setEditedExifData(null);
    setIsEditing(false);
    setError(null);
  };

  useEffect(() => {
    return () => {
      // Очистка при размонтировании компонента
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // --- Функция для сохранения отредактированных метаданных ---
  const saveEditedExifData = () => {
    // В реальном приложении здесь должна быть логика сохранения изменений и создания нового файла
    setExifData(editedExifData);
    setIsEditing(false);
    alert("В реальном приложении, здесь произошло бы сохранение изменений в изображении. Обратите внимание, что для полноценного изменения EXIF-данных требуется серверная часть или использование WebAssembly.");
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Image className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Считыватель EXIF-данных изображений'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {tool?.description || 'Анализируйте, просматривайте и редактируйте EXIF-метаданные ваших фотографий. Проверяйте настройки камеры, GPS-координаты и защищайте личную информацию перед публикацией.'}
              </p>
            </div>

            {/* --- Загрузчик изображений --- */}
            <ImageUploader 
              onFileSelect={analyzeExifData}
              isLoading={isLoading}
              error={error}
              resetAndLoadNew={resetAndLoadNew}
              exifData={exifData}
              imageUrl={imageUrl} // Добавляем этот проп
            />

            {/* --- Контент в зависимости от состояния --- */}
            {!exifData && !isLoading && !error && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mt-6">
                <div className="flex flex-col items-center text-center p-8">
                  <FileQuestion className="w-16 h-16 text-purple-500 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Загрузите изображение для начала анализа</h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mb-6">
                    Поддерживаются форматы JPG, JPEG, PNG, HEIC, TIFF. Анализ происходит прямо в вашем браузере, файлы не загружаются на сервер.
                  </p>
                </div>
              </div>
            )}

            {/* --- Вкладки для показа различных видов данных --- */}
            {exifData && (
              <div className="space-y-8 mt-6">
                {/* Навигация по вкладкам */}
                <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex overflow-x-auto scrollbar-thin">
                    <button
                      onClick={() => setActiveTab('general')}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'general'
                          ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Info className="w-4 h-4 inline mr-2" />
                      Основная информация
                    </button>
                    <button
                      onClick={() => setActiveTab('camera')}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'camera'
                          ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Camera className="w-4 h-4 inline mr-2" />
                      Камера и настройки
                    </button>
                    {exifData.location.latitude && (
                      <button
                        onClick={() => setActiveTab('location')}
                        className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                          activeTab === 'location'
                            ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Геолокация
                      </button>
                    )}
                    <button
                      onClick={() => setActiveTab('privacy')}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'privacy'
                          ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <UserX className="w-4 h-4 inline mr-2" />
                      Проверка приватности
                    </button>
                    <button
                      onClick={() => setActiveTab('edit')}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                        activeTab === 'edit'
                          ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-500'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Settings className="w-4 h-4 inline mr-2" />
                      Редактировать EXIF
                    </button>
                  </div>
                </div>

                {/* Содержимое вкладок */}
                {activeTab === 'general' && (
                  <ExifOverview 
                    exifData={exifData}
                    imageUrl={imageUrl}
                  />
                )}

                {activeTab === 'camera' && (
                  <ExifDetailsPanel 
                    exifData={exifData}
                    section="camera"
                  />
                )}

                {activeTab === 'location' && exifData.location.latitude && (
                  <LocationViewer 
                    location={exifData.location}
                    imageUrl={imageUrl}
                  />
                )}

                {activeTab === 'privacy' && (
                  <ExifPrivacyChecker 
                    exifData={exifData}
                  />
                )}

                {activeTab === 'edit' && (
                  <ExifEditingTools 
                    exifData={exifData}
                    editedExifData={editedExifData}
                    setEditedExifData={setEditedExifData}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    onSave={saveEditedExifData}
                  />
                )}
              </div>
            )}

            {/* --- Информационный контент о EXIF --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Недавно просмотренные файлы --- */}
            <RecentFilesSidebar 
              recentFiles={recentFiles}
              onFileSelect={analyzeExifData}
            />

            {/* --- Советы по приватности --- */}
            <PrivacyTipsSidebar />

            {/* --- Популярные камеры в России --- */}
            <PopularCamerasSidebar 
              popularCameras={popularCameras}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ExifDataReaderPage;